import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useAuthStore } from '@carouselgg/auth/store'
import { Card } from '@carouselgg/nexus/card'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { Badge } from '@carouselgg/nexus/badge'
import { toast } from '@carouselgg/nexus/toast'
import { getJoinLink, acceptJoinLink } from '../api/invites'
import type { InviteLinkPublic } from '../api/invites'
import styles from './InvitePage.module.css'

type PageState =
  | 'loading'
  | 'invalid'
  | 'expired'
  | 'revoked'
  | 'exhausted'
  | 'unauthenticated'
  | 'needs_phone_verification'
  | 'ready'
  | 'accepting'
  | 'accepted'

export function JoinPage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isAuthLoading = useAuthStore((s) => s.isLoading)
  const phoneVerified = useAuthStore((s) => s.phoneVerified)

  const [link, setLink] = useState<InviteLinkPublic | null>(null)
  const [state, setState] = useState<PageState>('loading')

  const computeReadyState = useCallback(() => {
    if (isAuthLoading) return 'loading' as PageState
    if (!isAuthenticated) return 'unauthenticated' as PageState
    if (!phoneVerified) return 'needs_phone_verification' as PageState
    return 'ready' as PageState
  }, [isAuthLoading, isAuthenticated, phoneVerified])

  useEffect(() => {
    if (!code) {
      setState('invalid')
      return
    }
    getJoinLink(code)
      .then((data) => {
        setLink(data)
        if (data.status !== 'active') {
          setState(data.status as PageState)
          return
        }
        setState(computeReadyState())
      })
      .catch(() => setState('invalid'))
  }, [code, computeReadyState])

  // Re-evaluate state when auth resolves
  useEffect(() => {
    if (!link || link.status !== 'active') return
    if (state === 'accepting' || state === 'accepted') return
    setState(computeReadyState())
  }, [link, computeReadyState, state])

  async function handleAccept() {
    if (!code) return
    setState('accepting')
    try {
      const result = await acceptJoinLink(code)
      setLink(result)
      setState('accepted')
      toast.success(`You've joined ${result.organization.name}!`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to join'
      toast.error(msg)
      setState(computeReadyState())
    }
  }

  if (state === 'loading') {
    return (
      <div className={styles.page}>
        <Text muted>Loading invitation...</Text>
      </div>
    )
  }

  if (state === 'invalid' || state === 'expired' || state === 'revoked' || state === 'exhausted') {
    const msg =
      state === 'expired'
        ? 'This invite link has expired.'
        : state === 'revoked'
          ? 'This invite link has been revoked.'
          : state === 'exhausted'
            ? 'This invite link has reached its use limit.'
            : 'This invite link is not valid.'
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.content}>
            <Text as="h1" size="xl" weight="bold">Invitation Unavailable</Text>
            <Text muted>{msg}</Text>
            <Button variant="primary" onClick={() => navigate('/')}>Go Home</Button>
          </div>
        </Card>
      </div>
    )
  }

  if (state === 'accepted' && link) {
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.content}>
            <Text as="h1" size="xl" weight="bold">Welcome!</Text>
            <Text>
              You've joined <strong>{link.organization.name}</strong> as a <strong>{link.org_role}</strong>
              {link.team && (
                <> and been added to team <strong>{link.team.name}</strong> as <strong>{link.team.role}</strong></>
              )}
              .
            </Text>
            <Button variant="primary" onClick={() => navigate(`/org/${link.organization.slug}`)}>
              Go to {link.organization.name}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  if (!link) return null

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.content}>
          {link.organization.logo_url && (
            <img
              src={link.organization.logo_url}
              alt={link.organization.name}
              className={styles.orgLogo}
            />
          )}
          <Text as="h1" size="xl" weight="bold">
            Join {link.organization.name}
          </Text>
          <div className={styles.details}>
            <div className={styles.detailRow}>
              <Text size="sm" muted>Organization</Text>
              <Text size="sm" weight="medium">{link.organization.name}</Text>
            </div>
            <div className={styles.detailRow}>
              <Text size="sm" muted>Type</Text>
              <Badge size="sm">{link.organization.type}</Badge>
            </div>
            <div className={styles.detailRow}>
              <Text size="sm" muted>Role</Text>
              <Badge size="sm" variant="info">{link.org_role}</Badge>
            </div>
            {link.team && (
              <>
                <div className={styles.detailRow}>
                  <Text size="sm" muted>Team</Text>
                  <Text size="sm" weight="medium">{link.team.name}</Text>
                </div>
                <div className={styles.detailRow}>
                  <Text size="sm" muted>Team Role</Text>
                  <Badge size="sm" variant="success">{link.team.role}</Badge>
                </div>
              </>
            )}
          </div>

          {state === 'unauthenticated' && (
            <>
              <Text size="sm" muted>Sign in or create an account to join.</Text>
              <div className={styles.actions}>
                <Button variant="primary" onClick={() => navigate(`/login?redirect=/join/${code}`)}>
                  Log In
                </Button>
                <Button variant="secondary" onClick={() => navigate(`/signup?redirect=/join/${code}`)}>
                  Create Account
                </Button>
              </div>
            </>
          )}

          {state === 'needs_phone_verification' && (
            <>
              <Text size="sm">
                Verify your phone number to join via a share link. This helps keep leagues free of
                duplicate accounts.
              </Text>
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  onClick={() => navigate(`/settings/sign-in?redirect=/join/${code}#phone`)}
                >
                  Verify Phone Number
                </Button>
              </div>
            </>
          )}

          {(state === 'ready' || state === 'accepting') && (
            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={handleAccept}
                disabled={state === 'accepting'}
              >
                {state === 'accepting' ? 'Joining...' : `Join ${link.organization.name}`}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
